/**
 * @module cercaliagl/BaseFeature
 */
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
import polyline from '@mapbox/polyline';
import { createUid, createCircleGeometry } from './util';
import BaseObject from './BaseObject';
import LonLat from './LonLat';
/**
 * @typedef {Object} BaseFeatureOptions
 * @property {string} [id] Id
 * @property {function(this:Marker): void} [onClick] Function to execute on marker click.
 * @property {function(this:Marker): void} [onDoubleClick] Function to execute on marker double click.
 * @property {function(this:Marker): void} [onMouseOut] Function to execute on Mouse Out.
 * @property {function(this:Marker): void} [onMouseOver] Function to execute on Mouse Over.
 * @property {function(this:Marker): void} [onMouseMove] Function to execute on Mouse Move.
 * @property {function(this:Marker): void} [onRightClick] Function to execute on marker right click.
 */
/**
 * @constant
 * @type {Map.<string, string>}
 */
var encodeMap = new Map([
    ['0', 'B'],
    ['1', 'C'],
    ['2', 'D'],
    ['3', 'T'],
    ['4', 'U'],
    ['5', 'V'],
    ['6', 'G'],
    ['7', 'H'],
    ['8', 'I'],
    ['9', '3'],
    ['A', '4'], ['a', 'i'],
    ['B', '5'], ['b', 'j'],
    ['C', 'w'], ['c', 'k'],
    ['D', 'x'], ['d', 'l'],
    ['E', 'y'], ['e', '6'],
    ['F', 'P'], ['f', '7'],
    ['G', 'Q'], ['g', '8'],
    ['H', 'R'], ['h', '9'],
    ['I', 'S'], ['i', 'A'],
    ['J', 'W'], ['j', 'a'],
    ['K', 'X'], ['k', 'b'],
    ['L', 'Y'], ['l', 'c'],
    ['M', 'Z'], ['m', 'd'],
    ['N', 'r'], ['n', 'J'],
    ['O', 's'], ['o', 'K'],
    ['P', 't'], ['p', 'L'],
    ['Q', 'u'], ['q', 'M'],
    ['R', 'v'], ['r', 'e'],
    ['S', 'm'], ['s', 'f'],
    ['T', 'n'], ['t', 'g'],
    ['U', 'o'], ['u', 'h'],
    ['V', 'p'], ['v', 'z'],
    ['W', 'q'], ['w', 'N'],
    ['X', '0'], ['x', 'O'],
    ['Y', '1'], ['y', 'E'],
    ['Z', '2'], ['z', 'F'],
]);
function translateEncodeDataToGooglePolyline(encodeData) {
    var encodedString = '';
    for (var i = 0; i < encodeData.length; i += 1) {
        var currentLetter = encodeMap.get(encodeData.charAt(i));
        encodedString += currentLetter !== null && currentLetter !== void 0 ? currentLetter : encodeData.charAt(i);
    }
    return encodedString;
}
/**
 * Encodes a google polyline
 * @param {string} googlePolyline Google polyline
 * @returns {string} Encoded polyline
 */
function translateGooglePolylineToEncodedData(googlePolyline) {
    var reverseEncodeMap = new Map(Array.from(encodeMap, function (_a) {
        var key = _a[0], value = _a[1];
        return [value, key];
    }));
    return googlePolyline
        .split('')
        .map(function (char) { var _a; return (_a = reverseEncodeMap.get(char)) !== null && _a !== void 0 ? _a : char; })
        .join('');
}
function decodeCoords_(encodeData, returnString) {
    if (returnString === void 0) { returnString = true; }
    var coords = polyline.decode(encodeData);
    if (!returnString) {
        return coords;
    }
    var wkt = '(';
    var lastPosition = coords.length - 1;
    for (var i = 0; i < lastPosition; i += 1) {
        wkt += "".concat(coords[i][1], " ").concat(coords[i][0], ", ");
    }
    wkt += "".concat(coords[lastPosition][1], " ").concat(coords[lastPosition][0], ")");
    return wkt;
}
function encodedDataToCircleCoords_(encodeData, extrapass) {
    if (extrapass === void 0) { extrapass = false; }
    var encodedString = extrapass ? translateEncodeDataToGooglePolyline(encodeData) : encodeData;
    return decodeCoords_(encodedString, false);
}
/**
 * @classdesc
 * Abstract class for internal purposes. Don't use it.
 */
var BaseFeature = /** @class */ (function (_super) {
    __extends(BaseFeature, _super);
    /**
     * @param {string} [id]
     */
    function BaseFeature(options) {
        if (options === void 0) { options = {}; }
        var _this = _super.call(this) || this;
        /**
         * @protected
         * @type {string}
         */
        _this.id_ = options.id ? options.id : createUid();
        /**
         * @protected
         * @type {import("./layer/Base").default}
         */
        _this.layer_ = null;
        /**
         * @type {function(this:Marker) : void|null}
         */
        _this.onClick = options.onClick ? options.onClick : null;
        /**
         * @type {Function|null}
         */
        _this.onDoubleClick = options.onDoubleClick ? options.onDoubleClick : null;
        /**
         * @type {Function|null}
         */
        _this.onRightClick = options.onRightClick ? options.onRightClick : null;
        /**
         * @type {Function|null}
         */
        _this.onMouseOver = options.onMouseOver ? options.onMouseOver : null;
        /**
         * @type {Function|null}
         */
        _this.onMouseMove = options.onMouseMove ? options.onMouseMove : null;
        /**
         * @type {Function|null}
         */
        _this.onMouseOut = options.onMouseOut ? options.onMouseOut : null;
        /**
         * @protected
         * @type {GeoJSON}
         */
        _this.geojson_ = null;
        /**
         * @protected
         * @type {import("./Map").default}
         */
        _this.map_ = null;
        return _this;
    }
    /**
     * @return {String} Id
     * @api
     */
    BaseFeature.prototype.getId = function () {
        return this.id_;
    };
    /**
     * @param {import("./layer/Base").default} layer Layer
     */
    BaseFeature.prototype.setLayer = function (layer) {
        this.layer_ = layer;
    };
    /**
     * @return {import("./layer/Base").default} Layer
     * @api
     */
    BaseFeature.prototype.getLayer = function () {
        return this.layer_;
    };
    /**
     * @return {GeoJSON} GeoJSON
     * @api
     */
    BaseFeature.prototype.getGeoJSON = function () {
        return this.geojson_;
    };
    /**
     * @return {string} GeoJSON
     * @api
     */
    BaseFeature.prototype.getGeometryType = function () {
        return this.geojson_.geometry ? this.geojson_.geometry.type : null;
    };
    /**
     * @return {import("./Map").default} Map
     * @api
     */
    BaseFeature.prototype.getMap = function () {
        return this.map_;
    };
    /**
     * @param {import('./Map').default} map Map
     * @api
     */
    BaseFeature.prototype.setMap = function (map) {
        this.map_ = map;
    };
    /**
     * @private
    */
    BaseFeature.prototype.encodedDataToLinestringWKT_ = function (encodeData, extrapass) {
        if (extrapass === void 0) { extrapass = false; }
        var encodedString = extrapass ? translateEncodeDataToGooglePolyline(encodeData) : encodeData;
        var coords = decodeCoords_(encodedString, this.encodedPrecision_);
        var wkt = "LINESTRING ".concat(coords);
        return wkt;
    };
    /**
     * @private
    */
    // eslint-disable-next-line class-methods-use-this
    BaseFeature.prototype.encodedDataToPointPosition_ = function (encodeData, extrapass) {
        if (extrapass === void 0) { extrapass = false; }
        var encodedString = extrapass ? translateEncodeDataToGooglePolyline(encodeData) : encodeData;
        var coords = decodeCoords_(encodedString, false);
        return new LonLat(coords[0][1], coords[0][0]);
    };
    /**
     * @private
     * @param {string} encodeData
     * @param {boolean} extrapass
    */
    BaseFeature.prototype.encodedDataToPolygonWKT_ = function (encodeData, extrapass) {
        if (extrapass === void 0) { extrapass = false; }
        var coords = this.decodeDataToPolygonWKT_(encodeData, extrapass);
        var wkt = "POLYGON (".concat(coords, ")");
        return wkt;
    };
    /**
     * @private
     * @param {Array.<string>} encodeData
     * @param {boolean} extrapass
    */
    BaseFeature.prototype.encodedDataToMultiPolygonWKT_ = function (encodeData, extrapass) {
        var _this = this;
        if (extrapass === void 0) { extrapass = false; }
        var wkt = 'MULTIPOLYGON (';
        encodeData.forEach(function (encodedString, index) {
            wkt += "(".concat(_this.decodeDataToPolygonWKT_(encodedString, extrapass), ")").concat(index < encodeData.length - 1 ? ',' : ')');
        });
        return wkt;
    };
    /**
     * @private
     * @param {string} encodeData
     * @param {boolean} extrapass
    */
    BaseFeature.prototype.decodeDataToPolygonWKT_ = function (encodeData, extrapass) {
        var _this = this;
        if (extrapass === void 0) { extrapass = false; }
        var wkt = '';
        var encodedDataSplitted = encodeData.split('-');
        encodedDataSplitted.forEach(function (encodedPolygon, index) {
            var encodedString = extrapass ? translateEncodeDataToGooglePolyline(encodedPolygon) : encodedPolygon;
            var coords = decodeCoords_(encodedString, _this.encodedPrecision_);
            wkt += "".concat(coords).concat(index < encodedDataSplitted.length - 1 ? ',' : '');
        });
        return wkt;
    };
    /**
     * @private
    */
    BaseFeature.getCircleGeometryFromEncodedData_ = function (encodeCenter, radius, vertices) {
        if (vertices === void 0) { vertices = 64; }
        var coords = encodedDataToCircleCoords_(encodeCenter, true);
        var center = new LonLat(coords[0][1], coords[0][0]);
        return createCircleGeometry(center, radius, vertices);
    };
    /**
   * Create circle geometry given a `center`, a radius and optionally number of `vertices`.
   * @api
   * @param {string} encodeCenter Center encoded on google polyline format.
   * @param {number} radius Radius in meters
   * @param {number} [vertices] Number of vertices. Default `64`.
   * @return {import("./util").GeometryGeoJSON} geometry
   */
    BaseFeature.googleEncodedCircleToGeometry = function (encodeCenter, radius, vertices) {
        if (vertices === void 0) { vertices = 64; }
        var coords = encodedDataToCircleCoords_(encodeCenter);
        var center = new LonLat(coords[0][1], coords[0][0]);
        return createCircleGeometry(center, radius, vertices);
    };
    return BaseFeature;
}(BaseObject));
export default BaseFeature;
//# sourceMappingURL=BaseFeature.js.map